import React, { useEffect, useState, useCallback } from 'react';
import VideoCard from '../../components/interviews/VideoCard';
import { isEmptyTranscript, truncateName } from '../../utils/dataUtils';
import HLSVideoPlayer from '../../components/interviews/HLSVideoPlayer';
import DeletedMediaPlaceholder from '../../components/interviews/DeletedMediaPlaceholder';
import { DeleteReason } from '../../types/interview';
import { Tooltip } from '@jobtarget/ui-library';
import { MediaService } from '../../api/MediaService';
import { AnalyticsService } from '../../api/AnalyticsService';
import { getAppByAppGuid } from '../../api/ApplicationService';
import Spinner from '../../components/Spinner';
import Icon from '../../components/ui/Icon/Icon';
import RotatingIcon from '../../components/ui/Icon/RotatingIcon';
import Toast from '../../components/Toast';
import JSZip from 'jszip';
import config from '../../data/config';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import Button from '../../components/Button';
import { LoggerService } from '@/api/LoggerService';

const resolveAnswerMedia = (answer: any): { url: string; type: 'video' | 'audio' } | null => {
  if (answer?.video) return { url: answer.video, type: 'video' };
  if (answer?.audio) return { url: answer.audio, type: 'audio' };
  return null;
};

interface OverlayProps {
  isOpen: boolean;
  onClose: () => void;
  selectedVideo: any;
  allVideos: any[];
  filters?: {
    selectedJob?: any;
    selectedDateRange?: string;
    appliedQuestions?: number | null;
  };
  isMobile?: boolean;
}

const VideoPlayerSection: React.FC<{
  videoUrl: string;
  contentType: 'video' | 'audio';
  // VI-564: dedicated prop for GTM id selection, independent of playback state
  interviewContentType?: 'video' | 'audio';
  thumbnailUrl?: string;
  isPlaying: boolean;
  onPlayToggle: () => void;
  isMobile?: boolean;
  isMuted?: boolean;
  setIsMuted?: (muted: boolean) => void;
  // VI-468: when true, render the deleted-media placeholder instead of
  // the thumbnail/play UI and skip the HLS player entirely.
  isDeleted?: boolean;
  deleteReason?: DeleteReason | null;
}> = React.memo(({ videoUrl, contentType, interviewContentType, thumbnailUrl, isPlaying, onPlayToggle, isMobile = false, isMuted = false, setIsMuted, isDeleted = false, deleteReason = null }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div style={{
      position: 'relative',
      borderRadius: '16px',
      overflow: 'hidden',
      aspectRatio: '16/9',
      maxHeight: isMobile ? '250px' : '450px',
      marginBottom: '1rem',
      width: '100%',
    }}>
      {isDeleted ? (
        <DeletedMediaPlaceholder variant="player" deleteReason={deleteReason} />
      ) : !isPlaying ? (
        <img
          src={thumbnailUrl || '/images/audio-placeholder.jpg'}
          alt="Content thumbnail"
          onError={(e) => { (e.target as HTMLImageElement).src = '/images/audio-placeholder.jpg'; }}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
        />
      ) : (
        contentType && (
          <HLSVideoPlayer
            apiUrl={videoUrl} 
            contentType={contentType}
            thumbnailUrl={thumbnailUrl}
            controls={true}
            autoplay={true}
            muted={isMuted}
            style={{ width: '100%', height: '100%' }}
            onLoadEnd={() => console.log(`${contentType} loaded`)}
            onError={(error) => console.error(`${contentType} error:`, error)}
          />
        )
      )}

      {!isDeleted && !isPlaying && (
        <>
          <div style={{
            position: 'absolute',
            bottom: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '99.5%',
            height: '50%',
            background: 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.4) 50%, transparent 100%)',
            pointerEvents: 'none',
            borderRadius: '16px',
          }} />

          <div 
            id={interviewContentType === 'audio' ? 'vi_overlay_audio_play' : 'vi_overlay_video_play'}
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              cursor: 'pointer'
            }}
            onClick={() => {
              const buttonId = interviewContentType === 'audio' ? 'vi_overlay_audio_play' : 'vi_overlay_video_play';
              console.warn('[GA] overlay_play_clicked →', buttonId);
              onPlayToggle();
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <i 
              className="fas fa-play"
              style={{
                color: 'white',
                opacity: isHovered ? 1 : 0.85,
                fontSize: isMobile ? '48px' : '80px',
                transition: 'all 0.2s ease',
                filter: isHovered ? 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.3))' : 'none'
              }}
            />
          </div>

          <div style={{
            position: 'absolute',
            bottom: '0.5rem',
            right: '0.5rem',
            padding: '0.5rem',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
          onClick={(e) => {
            e.stopPropagation();
            setIsMuted?.(!isMuted);
          }}
          >
            <Icon
              icon={isMuted ? "fas fa-volume-xmark" : "fas fa-volume-high"}
              style={{
                color: 'white',
                fontSize: '20px',
                opacity: 1,
                transition: 'opacity 0.2s ease'
              }}
              ariaHidden
            />
          </div>
        </>
      )}
    </div>
  );
});

const ApplicantInfoSection: React.FC<{
  applicantName: string;
  jobTitle: string;
  dateApplied: string;
  isMobile: boolean;
}> = ({ applicantName, jobTitle, dateApplied, isMobile }) => {
  const shouldDisplayNameTooltip = applicantName.length > 20;
  const shouldDisplayjobTitleTooltip = jobTitle.length > 32;

  return (
    <div style={{  
        gap: '0.3rem',
        display: 'flex',  
        flexDirection: 'column',
        flex: 1
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          {shouldDisplayNameTooltip ? (
            <Tooltip message={truncateName(applicantName,32)} position="top">
              <h2 style={{ 
                fontSize: '1.5rem', 
                fontWeight: '500',
                margin: 0,
                cursor: 'default'
              }}>
                {truncateName(applicantName, 20)}
              </h2>
            </Tooltip>
          ) : (
            <h2 style={{ 
              fontSize: '1.5rem', 
              fontWeight: '500',
              margin: 0,
              cursor: 'default'
            }}>
              {truncateName(applicantName, 20)}
            </h2>
          )}
        </div>

          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: '0.4rem',
            cursor: 'default'
          }}>
            {shouldDisplayjobTitleTooltip ? (
              <Tooltip message={jobTitle} position="top">
                <p style={{ 
                  color: '#3F4254',
                  fontSize: '0.875rem',
                  margin: 0,
                }}>
                  Applied for <span style={{ fontWeight: '600' }}>{truncateName(jobTitle, isMobile ? 50 : 32)}</span>
                </p>
              </Tooltip>
            ) : (
              <p style={{ 
                color: '#3F4254',
                fontSize: '0.875rem',
                margin: 0,
              }}>
                Applied for <span style={{ fontWeight: '600' }}>{truncateName(jobTitle, isMobile ? 50 : 32)}</span>
              </p>
            )}
            <p style={{ 
              color: '#9ca3af',
              fontSize: '0.875rem',
              margin: 0,
            }}>
              • {dateApplied}
            </p>
        </div>
      </div>
  );
};

const ActionButtonsSection: React.FC<{
  onViewApplication: () => void;
  onDownloadVideo: () => void;
  onDownloadQuestion: (index: number) => void;
  onQuestionSelect: (index: number) => void; 
  downloadingAll: boolean;
  downloadingQuestions: Set<number>;
  isRedirecting: boolean;
  selectedQuestionIndex: number;
  totalQuestions: number;
  contentType: string;
  // VI-468: when true, every download path is disabled because the
  // backing media URLs are null. View Application stays enabled — the
  // recruiter still needs the app context.
  isDeleted?: boolean;
}> = ({ 
  onViewApplication, 
  onDownloadVideo, 
  onDownloadQuestion,
  onQuestionSelect,
  downloadingAll, 
  downloadingQuestions,
  isRedirecting,
  selectedQuestionIndex,
  totalQuestions,
  contentType,
  isDeleted = false
}) => {
  const isDownloadBlocked = isDeleted || downloadingAll || downloadingQuestions.size > 0;
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = React.useRef<HTMLButtonElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current && 
        dropdownRef.current && 
        !menuRef.current.contains(event.target as Node) &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  return (
    <div style={{ 
      display: 'flex', 
      gap: '0.5rem',
      height: '80%', 
      alignItems: 'stretch' 
    }}>
      <style>{`
        .overlay-dropdown-menu.dropdown-menu {
          position: absolute !important;
          top: 100% !important;
          left: 0 !important;
          bottom: auto !important;
          transform: none !important;
          margin-top: 0.125rem !important;
          z-index: 10001 !important;
          background-color: white;
          border-radius: 0.25rem;
          box-shadow: 0 0.5rem 1rem rgba(0,0,0,.175);
        }
        .overlay-dropdown-menu.dropdown-menu.show {
          display: block !important;
        }
        .overlay-dropdown-menu .dropdown-item {
          padding: 0.5rem 1rem;
          clear: both;
          font-weight: 400;
          color: #212529;
          text-align: inherit;
          white-space: nowrap;
          background-color: transparent;
          border: 0;
          display: block;
          width: 100%;
        }
        .overlay-dropdown-menu .dropdown-item:hover:not(:disabled) {
          background-color: #f8f9fa;
        }
        .overlay-dropdown-menu .dropdown-item:focus {
          outline: none;
          background-color: #f8f9fa;
        }
      `}</style>
      <Button 
        text={
          <span style={{ display: 'flex', alignItems: 'center' }}>
            {isRedirecting ? (
              <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Spinner size="sm" />
                Redirecting...
              </div>
              </>
            ) : (
              'View Application'
            )}
          </span>
        } 
        variant="outline-primary"
        onClick={onViewApplication}
        disabled={isRedirecting}
        style={{
          flex: 1, 
          height: '100%', 
          minHeight: '40px',
          cursor: isRedirecting ? 'not-allowed' : 'pointer'
        }}
      />
      <div className="dropdown" style={{ flex: 1, display: 'flex', position: 'relative', zIndex: 1000 }}>
        <button
          className="btn btn-primary"
          type="button"
          disabled={isDownloadBlocked}
          onClick={() => {
            if (isDeleted) return;
            onDownloadQuestion(selectedQuestionIndex);
          }}
          style={{
            flex: 1,
            height: '100%',
            minHeight: '40px',
            cursor: isDownloadBlocked ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            paddingLeft: '1rem',
            paddingRight: '1',
            paddingTop: 0,
            paddingBottom: 0,
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
            borderRight: 'none'
          }}
        >
          <span style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            paddingTop: '0.5rem',
            paddingBottom: '0.5rem',
          }}>
            {downloadingAll ? (
              <>
                <Spinner size="sm" color="light" />
                <span>Downloading...</span>
              </>
            ) : downloadingQuestions.has(selectedQuestionIndex) ? (
              <>
                <Spinner size="sm" color="light" />
                <span>Downloading...</span>
              </>
            ) : (
              <>
                <span>Download Question #{selectedQuestionIndex + 1}</span>
              </>
            )}
          </span>
        </button>
        
        <button
          ref={dropdownRef}
          className="btn btn-primary"
          type="button"
          onClick={() => {
            if (isDeleted) return;
            setIsDropdownOpen(!isDropdownOpen);
          }}
          disabled={isDownloadBlocked}
          style={{
            height: '100%',
            minHeight: '40px',
            cursor: isDownloadBlocked ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            paddingLeft: '0.75rem',
            paddingRight: '0.75rem',
            paddingTop: 0,
            paddingBottom: 0,
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
            borderLeft: '1px solid rgba(255, 255, 255, 0.4)',
            minWidth: '40px'
          }}
        >
          <i className="fal fa-chevron-down" style={{ fontSize: '0.875rem' }}></i>
        </button>
        <div 
          ref={menuRef} 
          className={`dropdown-menu overlay-dropdown-menu ${isDropdownOpen ? 'show' : ''}`}
          style={{ 
            width: '100%', 
            maxHeight: '300px', 
            overflowY: 'auto',
            display: isDropdownOpen ? 'block' : 'none'
          }}
        >
        <button
          className="dropdown-item"
          type="button"
          onClick={() => {
            onQuestionSelect(0);
            onDownloadVideo();
            setIsDropdownOpen(false);
          }}
          disabled={downloadingAll}
          style={{
            cursor: downloadingAll ? 'not-allowed' : 'pointer',
            opacity: downloadingAll ? 0.6 : 1
          }}
        >
          {downloadingAll ? (
            <>
              <Spinner size="sm" />
              {' '}Downloading Full {contentType === 'video' ? 'Video' : 'Audio'}...
            </>
          ) : (
            <>
              {' '}Full {contentType === 'video' ? 'Video' : 'Audio'}
            </>
          )}
        </button>
        {Array.from({ length: totalQuestions }, (_, index) => (
          <button
            key={index}
            className="dropdown-item"
            type="button"
            onClick={() => {
              onQuestionSelect(index);
              onDownloadQuestion(index);
              setIsDropdownOpen(false);
            }}
            disabled={downloadingQuestions.has(index)}
            style={{
              cursor: downloadingQuestions.has(index) ? 'not-allowed' : 'pointer',
              opacity: downloadingQuestions.has(index) ? 0.6 : 1,
              fontWeight: index === selectedQuestionIndex ? '600' : '400'
            }}
          >
            {downloadingQuestions.has(index) ? (
              <>
                <Spinner size="sm" />
                {' '}Downloading Question #{index + 1}...
              </>
            ) : (
              <>
                {' '}Question #{index + 1}
              </>
            )}
          </button>
        ))}
      </div>
    </div>
  </div>
  );
};

const NavigationControlsSection: React.FC<{
  currentIndex: number;
  totalCount: number;
  onPrevious: () => void;
  onNext: () => void;
  disabled?: boolean;
}> = ({ currentIndex, totalCount, onPrevious, onNext, disabled = false }) => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    marginBottom: '0.5rem'
  }}>
    <button
      onClick={onPrevious}
      disabled={disabled || currentIndex === 0}
      style={{
        background: 'transparent',
        border: 'none',
        cursor: (disabled || currentIndex === 0) ? 'not-allowed' : 'pointer',
        fontSize: '0.875rem',
        color: (disabled || currentIndex === 0) ? '#adb5bd' : '#2F86DD',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        fontWeight: '400'
      }}
    >
      <i className="fal fa-chevron-left"></i>
      Prev
    </button>
  
    <span style={{
      fontSize: '0.875rem',
      textAlign: 'center',
      fontWeight: 500
    }}>
      {currentIndex + 1} of {totalCount} submission
    </span>
  
    <button
      onClick={onNext}
      disabled={disabled || currentIndex === totalCount - 1}
      style={{
        background: 'transparent',
        border: 'none',
        cursor: (disabled || currentIndex === totalCount - 1) ? 'not-allowed' : 'pointer',
        fontSize: '0.875rem',
        color: (disabled || currentIndex === totalCount - 1) ? '#adb5bd' : '#2F86DD',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        fontWeight: '400'
      }}
    >
      Next
      <i className="fal fa-chevron-right"></i>
    </button>
  </div>
);

const ApplicantThumbnailsSection: React.FC<{
  videoData: any[];
  currentIndex: number;
  onApplicantSelect: (index: number) => void;
  isMobile?: boolean;
}> = ({ videoData, currentIndex, onApplicantSelect, isMobile = false }) => {
  const getDisplayedVideos = () => {
    if (videoData.length <= 3) {
      return videoData.map((video, index) => ({ video, originalIndex: index }));
    }

    const displayed = [];
    
    const startIndex = Math.max(0, currentIndex - 1);
    const endIndex = Math.min(videoData.length - 1, startIndex + 2);
    
    const actualStartIndex = endIndex === videoData.length - 1 && videoData.length >= 3 
      ? Math.max(0, videoData.length - 3) 
      : startIndex;

    for (let i = actualStartIndex; i <= Math.min(actualStartIndex + 2, videoData.length - 1); i++) {
      displayed.push({ video: videoData[i], originalIndex: i });
    }

    return displayed;
  };

  const displayedVideos = getDisplayedVideos();

  return (
    <div style={{ marginBottom: '0rem' }}>
      <div style={{
        display: 'flex',
        gap: isMobile ? '0.75rem' : '0.5rem',
        width: '100%',
        justifyContent: displayedVideos.length === 1 ? 'center' : 'flex-start',
        borderRadius: '8px',
      }}>
        {displayedVideos.map(({ video, originalIndex }) => (
          <div
            key={video.id}
            style={{
              flex: displayedVideos.length === 1 ? 'none' : 1,
              maxWidth: displayedVideos.length === 1 ? '300px' : 'none',
              minWidth: displayedVideos.length > 1 ? '0' : 'auto',
              opacity: originalIndex === currentIndex ? 1 : 0.8,
              transform: originalIndex === currentIndex ? 'scale(1.02)' : 'scale(0.98)',
              transition: 'all 0.2s ease'
            }}
          >
            <VideoCard 
              applicantName={video.applicantName}
              jobTitle="" 
              dateApplied={video.dateApplied}
              thumbnailUrl={video.thumbnailUrl}
              onClick={() => onApplicantSelect(originalIndex)}
              isMobile={isMobile}
              isLoading={false}
              videoId={video.id}
              compact={true}
              isSelected={originalIndex === currentIndex}
              // VI-468: forward deletion state — without this the strip
              // card falls back to the API thumbnail (or audio
              // placeholder) for deleted interviews instead of our
              // placeholder UI.
              isDeleted={video.isDeleted ?? video.interview?.is_deleted === true}
              deleteReason={video.deleteReason ?? video.interview?.deleteReason ?? null}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const RightSidebarSection: React.FC<{
  activeTab: 'questions' | 'transcript';
  onTabChange: (tab: 'questions' | 'transcript') => void;
  responses: any[];
  selectedQuestionIndex: number;
  onQuestionSelect: (index: number) => void;
  selectedResponse?: any;
  onDownloadQuestion: (index: number, response: any) => void;
  downloadingQuestions: Set<number>;
  isMobile?: boolean;
  contentType: string;
  showContent: boolean;
  setShowContent: (show: boolean) => void;
  // VI-468: when true, swap the per-question pill to "Answer Removed"
  // and hide the Show Transcript accordion entirely.
  isDeleted?: boolean;
}> = ({ 
  activeTab, 
  onTabChange, 
  responses, 
  selectedQuestionIndex, 
  onQuestionSelect, 
  selectedResponse,
  onDownloadQuestion,
  downloadingQuestions,
  isMobile = false,
  contentType = 'video',
  showContent = true,
  setShowContent = () => {},
  isDeleted = false
}) => {
  const [openAccordions, setOpenAccordions] = useState<Set<number>>(new Set());

  const toggleAccordion = (index: number) => {
    setOpenAccordions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  useEffect(() => {
    setOpenAccordions(new Set());
  }, [responses]);

  return (
    <div style={{ 
      flex: isMobile ? 'none' : (showContent ? '1' : '0'),
      borderRadius: '8px',
      height: isMobile ? 'auto' : 'fit-content',
      overflow: 'visible',
      transition: 'flex 0.3s ease, min-width 0.3s ease',
      border: '1px solid #dee2e6',
      backgroundColor: 'white',
      display: showContent ? 'block' : 'inline-block'
    }}>
      <div style={{ 
        backgroundColor: '#f8f9fa',
        paddingLeft: showContent ? '0.875rem' : '0.5rem', 
        paddingRight: showContent ? '0.875rem' : '0.5rem',
        paddingTop: showContent ? '0.2rem' : '0.25rem',
        paddingBottom: '0.3rem',
        borderBottom: showContent ? '1px solid #dee2e6' : 'none',
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px',
        borderBottomLeftRadius: showContent ? '0' : '8px',
        borderBottomRightRadius: showContent ? '0' : '8px',
        whiteSpace: 'nowrap',
        width: isMobile ? '100%' : (showContent ? '100%' : 'fit-content'),
        display: isMobile ? 'block' : (showContent ? 'block' : 'inline-block')
      }}>
        <button
          style={{
            background: 'none',
            border: 'none',
            fontSize: '1rem',
            fontWeight: '400',
            cursor: 'default',
            padding: '0.5rem 0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: isMobile ? 'space-between' : (showContent ? 'space-between' : 'center'),
            width: isMobile ? '100%' : (showContent ? '100%' : 'auto'),
            gap: '0.5rem'
          }}
        >
          <span style={{ fontWeight: '300', fontSize: '1rem' }}>Questions</span>
          <Icon
            icon={showContent ? "fal fa-arrow-right-to-line" : "fal fa-arrow-left-to-line"}
            style={{
              fontSize: isMobile ? '1rem' : '1.2rem',
              color: '#6C757D',
              marginLeft: isMobile ? 'auto' : (showContent ? 'auto' : '0.5rem'),
              cursor: 'pointer',
              transition: 'transform 0.3s ease',
              flexShrink: 0
            }}
            onClick={() => setShowContent(!showContent)}
            ariaLabel="Toggle questions panel"
          />
        </button>
      </div>

      {showContent && (
        <div style={{ 
          maxHeight: isMobile ? '400px' : '650px',
          overflow: 'auto',
          borderBottomLeftRadius: '8px',
          borderBottomRightRadius: '8px',
        }}>
          <div style={{ padding: '1.35rem' }}>
            {responses && responses.length > 0 ? (
              <div className="accordion" id="questionsAccordion">
                {responses.map((response: any, index: number) => {
                  const isOpen = openAccordions.has(index);
                  const hasTranscript = !isEmptyTranscript(response?.answer?.text);
                  const accordionId = `collapse${index}`;
                  const headingId = `heading${index}`;

                  return (
                    <div 
                      key={index}
                      className="card"
                      style={{
                        marginBottom: '0.5rem',
                        border: selectedQuestionIndex === index ? '2px solid #2F86DD' : '1px solid #dee2e6',
                        borderRadius: '6px',
                      }}
                    >
                      <div 
                        style={{
                          padding: '0.75rem',
                          cursor: 'pointer',
                          backgroundColor: 'transparent'
                        }}
                        onClick={() => {
                          onQuestionSelect(index);
                        }}
                      >
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          marginBottom: '0.5rem'
                        }}>
                          <span style={{ 
                            fontWeight: '400',
                            fontSize: isMobile ? '0.875rem' : '1rem',
                            lineHeight: '1.4',
                            color: 'black',
                            flex: 1
                          }}>
                            {index + 1}. {response.question?.text || 'No question text available'}
                          </span>
                        </div>
                        
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}>
                          {isDeleted ? (
                            <span
                              className="badge badge-pill"
                              style={{
                                backgroundColor: '#F3F4F6',
                                color: '#6C757D',
                                border: 'none',
                                fontWeight: '400',
                                fontSize: '0.75rem',
                                marginTop: '1rem',
                                opacity: 0.5,
                                padding: '0.25rem 0.5rem',
                              }}
                            >
                              Answer Removed
                            </span>
                          ) : (
                            <span
                              className="badge badge-pill"
                              style={{
                                backgroundColor: '#E3FBFA',
                                color: '#0F6E69',
                                border: 'none',
                                fontWeight: '400',
                                fontSize: '0.75rem',
                              }}
                            >
                              Answer Available
                            </span>
                          )}
                        </div>
                      </div>

                      {!isDeleted && (
                      <div className="card-header" id={headingId} style={{ 
                        padding: '0',
                        backgroundColor: 'transparent',
                        border: 'none',
                        borderTop: '1px solid #ebedf3'
                      }}>
                        <h3 className="mb-0" data-toc-ignore="true">
                          <button
                            className={`btn btn-link btn-block text-left ${isOpen ? '' : 'collapsed'}`}
                            type="button"
                            aria-expanded={isOpen}
                            aria-controls={accordionId}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleAccordion(index);
                            }}
                            style={{
                              width: '100%',
                              textAlign: 'left',
                              padding: '0.75rem 1rem',
                              border: 'none',
                              backgroundColor: 'transparent',
                              cursor: 'pointer',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              textDecoration: 'none'
                            }}
                            onMouseDown={(e) => {
                              e.preventDefault();
                            }}
                          >
                            <span style={{ 
                              fontSize: '0.875rem', 
                              fontWeight: '400',
                              color: 'black'
                            }}>
                              {isOpen ? 'Hide Transcript' : 'Show Transcript'}
                            </span>
                            <RotatingIcon
                              icon="fal fa-chevron-down"
                              isOpen={isOpen}
                              style={{
                                fontSize: isMobile ? '1rem' : '1.2rem',
                                color: '#6C757D'
                              }}
                            />
                          </button>
                        </h3>
                      </div>
                      )}

                      {!isDeleted && (
                      <div 
                        id={accordionId}
                        className={`collapse ${isOpen ? 'show' : ''}`}
                        aria-labelledby={headingId}
                        data-parent="#questionsAccordion"
                        style={isOpen ? {} : { display: 'none' }}
                      >
                        <div className="card-body" style={{
                          padding: '0.75rem',
                          paddingTop: '0.5rem',
                          borderTop: 'none'
                        }}>
                          {hasTranscript ? (
                            <div style={{
                              borderRadius: '6px',
                              fontSize: isMobile ? '0.8rem' : '0.875rem',
                              lineHeight: '1.5',
                              color: 'black'
                            }}>
                              {response.answer.text}
                            </div>
                          ) : (
                            <div style={{
                              borderRadius: '6px',
                              fontSize: isMobile ? '0.8rem' : '0.875rem',
                              lineHeight: '1.5',
                              color: '#6c757d',
                              fontStyle: 'italic'
                            }}>
                              No transcriptions available.
                            </div>
                          )}
                        </div>
                      </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#6c757d' }}>
                No questions available
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const ExpandedInterviewPageOverlay: React.FC<OverlayProps> = ({ 
  isOpen, 
  onClose, 
  selectedVideo, 
  allVideos,
  filters,
  isMobile: propIsMobile
}) => {
  const [videoData, setVideoData] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<'questions' | 'transcript'>('questions');
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState<number>(filters?.appliedQuestions ?? 0);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [videoPlayerState, setVideoPlayerState] = useState({
    isPlaying: false,
    videoUrl: '',
    contentType: 'video' as 'video' | 'audio'
  });
  const [isMuted, setIsMuted] = useState(false);
  const [downloadingQuestions, setDownloadingQuestions] = useState<Set<number>>(new Set());
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [successAlert, setSuccessAlert] = useState({
    show: false,
    message: '',
    type: 'success' as 'success' | 'error' | 'warning' | 'info',
    title: ''
  });
  const [isMobile, setIsMobile] = useState(propIsMobile ?? window.innerWidth < 835);
  const isLargeDesktop = window.innerWidth >= 1920;
  const [showContent, setShowContent] = useState(true);
  const [currentInterviewIndex, setCurrentInterviewIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 835);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Trigger animation on open
  useEffect(() => {
    if (isOpen) {
      // Small delay to trigger animation
      requestAnimationFrame(() => {
        setIsAnimating(true);
      });
    } else {
      setIsAnimating(false);
      setSuccessAlert({
        show: false,
        message: '',
        type: 'success',
        title: ''
      });
    }
  }, [isOpen]);

  // Lock body scroll when modal is open and cleanup on close
  useEffect(() => {
    if (isOpen) {
      // Save current overflow style
      const originalOverflow = document.body.style.overflow;
      // Prevent body scroll
      document.body.style.overflow = 'hidden';
      
      // Restore on cleanup
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    } else {
      // Cleanup when overlay is closed
      const exitPiP = async () => {
        if (document.pictureInPictureElement) {
          try {
            await document.exitPictureInPicture();
          } catch (error) {
            console.error('Failed to exit PiP on cleanup:', error);
          }
        }
      };
      
      exitPiP();
      // Reset mute state when overlay closes
      setIsMuted(false);
      // Stop playing video
      setVideoPlayerState(prev => ({
        ...prev,
        isPlaying: false
      }));
    }
  }, [isOpen]);

  // Initialize video data
  useEffect(() => {
    if (isOpen && allVideos && allVideos.length > 0) {
      setVideoData(allVideos);
      const index = allVideos.findIndex(v => v.id === selectedVideo.id);
      setCurrentInterviewIndex(index >= 0 ? index : 0);
    }
  }, [isOpen, allVideos, selectedVideo]);

  // Reset state when interview changes
  useEffect(() => {
    setVideoPlayerState({
      isPlaying: false,
      videoUrl: '',
      contentType: 'video'
    });
    // If there's a filter applied, use that; otherwise default to 0
    setSelectedQuestionIndex(filters?.appliedQuestions ?? 0);
  }, [currentInterviewIndex, filters?.appliedQuestions]);

  // Stop playing when question changes
  useEffect(() => {
    if (videoPlayerState.isPlaying) {
      setVideoPlayerState(prev => ({
        ...prev,
        isPlaying: false
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedQuestionIndex]);

  const currentInterview = videoData[currentInterviewIndex];
  const currentResponses = currentInterview?.interview?.responses || [];
  const selectedResponse = currentResponses[selectedQuestionIndex];

  // VI-468: derive deletion state from the current interview so we can
  // gate the player, downloads, and per-question pills + transcript UI.
  // Read directly off the underlying interview object (set by VI-491
  // backend), not the videoData mirror, so the overlay stays correct
  // even if a future caller forgets to forward isDeleted on videoData.
  const isCurrentInterviewDeleted = currentInterview?.interview?.is_deleted === true;
  const currentDeleteReason: DeleteReason | null =
    currentInterview?.interview?.deleteReason ?? null;

  // Set title
  useEffect(() => {
    if (currentInterview) {
    //   setTitle(`${currentInterview.jobTitle}`);
    //   setIcon('/svgs/expanded-view-icon.svg');
    }
  }, []);

  const navigateToInterview = (index: number) => {
    if (index >= 0 && index < videoData.length) {
      setCurrentInterviewIndex(index);
    }
  };

  const goToPrevious = () => {
    if (currentInterviewIndex > 0) {
      navigateToInterview(currentInterviewIndex - 1);
    }
  };

  const goToNext = () => {
    if (currentInterviewIndex < videoData.length - 1) {
      navigateToInterview(currentInterviewIndex + 1);
    }
  };

  const handleDownloadQuestion = async (questionIndex: number, response: any) => {
    const media = resolveAnswerMedia(response.answer);
    const rawUrl = media?.url;
    const currentContentType = media?.type || 'video';
      
    if (!rawUrl) {
      setSuccessAlert({
        show: true,
        message: 'No media URL found for download',
        type: 'success',
        title: 'No media URL found for download'
      });
      return;
    }
  
    setDownloadingQuestions(prev => new Set(prev).add(questionIndex));
  
    try {
      await MediaService.downloadMedia(
        rawUrl, 
        currentContentType, 
        questionIndex, 
        currentInterview.applicantName
      );

      setSuccessAlert({
        show: true,
        message: `Question ${questionIndex + 1} for ${currentInterview.applicantName} was downloaded successfully!`,
        type: 'success',
        title: 'Download successful'
      });
    } catch (error: any) {
      setSuccessAlert({
        show: true,
        message: `Question ${questionIndex + 1} for ${currentInterview.applicantName} was not downloaded. Please try again.`,
        type: 'error',
        title: 'Download unsuccessful'
      });
    } finally {
      setDownloadingQuestions(prev => {
        const newSet = new Set(prev);
        newSet.delete(questionIndex);
        return newSet;
      });
    }
  };

  const handleDownloadAllVideos = async () => {
    if (!currentInterview || !currentResponses.length) {
      setSuccessAlert({
        show: true,
        message: 'No videos available to download',
        type: 'success',
        title: 'No videos available to download'
      });
      return;
    }

    setDownloadingAll(true);
    const labelType = currentInterview.contentType || 'video';

    try {
      const downloadPromises = currentResponses.map((response: any, index: number) => {
        const media = resolveAnswerMedia(response.answer);

        if (!media) {
          return Promise.resolve(null);
        }

        return MediaService.downloadMediaAsBlob(
          media.url,
          media.type,
          index,
          currentInterview.applicantName
        );
      });

      const results = await Promise.all(downloadPromises);
      const successfulDownloads = results.filter(result => result !== null) as Array<{ blob: Blob, filename: string }>;
      
      if (successfulDownloads.length === 0) {
        setSuccessAlert({
          show: true,
          message: 'Failed to download any files. Please try again.',
          type: 'success',
          title: 'Failed to download any files'
        });
        return;
      }

      const zip = new JSZip();
      
      successfulDownloads.forEach(({ blob, filename }) => {
        zip.file(filename, blob);
      });

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const zipFilename = `${currentInterview.applicantName.replace(/\s+/g, '-')}-screener-${labelType}s.zip`;
      const downloadLink = document.createElement('a');
      downloadLink.href = URL.createObjectURL(zipBlob);
      downloadLink.download = zipFilename;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(downloadLink.href);
      
      setSuccessAlert({
        show: true,
        message: `All ${successfulDownloads.length} ${labelType}s for ${currentInterview.applicantName} zipped and downloaded successfully!`,
        type: 'success',
        title: 'Download successful'
      });
    } catch (error: any) {
      setSuccessAlert({
        show: true,
        message: `Download failed: ${error.message}`,
        type: 'error',
        title: 'Download failed'
      });
    } finally {
      setDownloadingAll(false);
    }
  };

  const handleViewApplication = async () => {
    try {
      setIsRedirecting(true);
      console.log("Redirecting to Candidate Manager");
      // setSuccessAlert({
      //   show: true,
      //   message: 'Redirecting to Candidate Manager, please wait!',
      //   type: 'success',
      //   title: 'Redirecting...'
      // });
      let applicantId: number | undefined;

      try {
        const analyticsResponse = await AnalyticsService.getApplicationFromAnalytics(currentInterview?.id);
        applicantId = analyticsResponse.data?.applicantId;
      } catch {
        LoggerService.error("Analytics lookup failed, falling back to Job Application API");
      }

      if (!applicantId) {
        LoggerService.info("No applicantId found in Analytics Response, checking in Job Application DB Apps Table");
        const appResponse = await getAppByAppGuid(currentInterview?.id);
        applicantId = appResponse?.metadata?.applicantId;
        LoggerService.info("ApplicantId found in Job Application DB Apps Table", applicantId);
      }

      if (!applicantId) {
        LoggerService.error('Could not find applicant information for this interview.');
        return;
      }

      const candidateManagerUrl = `${config.CANDIDATE_MANAGER_URL}/candidate-manager/details?candidateId=${applicantId}&tab=resume&showMessaging=false`;
      const newWindow = window.open(candidateManagerUrl, '_blank');

      if (!newWindow) {
        console.warn('Popup blocked! Please enable popups for this site to view applications in a new tab.');
        return;
      }
    } catch(err: any) {
      const fallbackUrl = `${config.CANDIDATE_MANAGER_URL}`;
      const newWindow = window.open(fallbackUrl, '_blank');
      
      if (!newWindow) {
        console.warn('Popup blocked! Please enable popups for this site to view applications in a new tab.');
        // setSuccessAlert({
        //   show: true,
        //   message: 'Popup blocked! Please enable popups for this site to view applications in a new tab.',
        //   type: 'success',
        //   title: 'Popup Blocked'
        // });
      }
    } finally {
      setIsRedirecting(false);
    }
  };

  const handlePlayToggle = useCallback(() => {
    const interview = videoData[currentInterviewIndex];
    if (!interview || !interview.interview?.responses) return;

    // VI-468: deleted interviews have null media URLs. The placeholder
    // UI hides the play button, but keyboard shortcuts in this overlay
    // can still trigger play — guard here as defense in depth.
    if (interview.interview.is_deleted === true) return;

    if (!videoPlayerState.isPlaying) {
      const responses = interview.interview.responses;
      const media = resolveAnswerMedia(responses[selectedQuestionIndex]?.answer);

      if (media) {
        setVideoPlayerState({
          isPlaying: true,
          videoUrl: media.url,
          contentType: media.type
        });
      }
    } else {
      setVideoPlayerState(prev => ({ ...prev, isPlaying: false }));
    }
  }, [currentInterviewIndex, videoData, videoPlayerState.isPlaying, selectedQuestionIndex]);

  const handleTabChange = (tab: 'questions' | 'transcript') => {
    setActiveTab(tab);
    setShowContent(true);
  };

  const handleClose = async () => {
    // Exit Picture-in-Picture mode if active
    if (document.pictureInPictureElement) {
      try {
        await document.exitPictureInPicture();
      } catch (error) {
        console.error('Failed to exit PiP:', error);
      }
    }

    // Reset mute state
    setIsMuted(false);

    // Stop playing video
    setVideoPlayerState(prev => ({
      ...prev,
      isPlaying: false
    }));

    setIsAnimating(false);
    setShowContent(true);
    // Wait for animation to complete before actually closing
    setTimeout(() => {
      onClose();
    }, 300); // Match the transition duration
  };

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLInputElement || 
          event.target instanceof HTMLTextAreaElement || 
          event.target instanceof HTMLSelectElement) {
        return;
      }

      if (event.key === 'Escape') {
        handleClose();
        return;
      }

      // Space bar for play/pause
      if (event.key === ' ' || event.key === 'Spacebar') {
        event.preventDefault();
        handlePlayToggle();
        return;
      }

      if (videoData.length <= 1) {
        return;
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goToPrevious();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        goToNext();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, currentInterviewIndex, videoData.length, handlePlayToggle, goToPrevious, goToNext, handleClose]);

  if (!isOpen) return null;

  return (
    <>
      {/* Toast - Rendered outside modal */}
      {successAlert.show && (
        <Toast
          isOpen={successAlert.show}
          onClose={() => setSuccessAlert({ show: false, message: '', type: 'success', title: '' })}
          message={successAlert.message}
          type={successAlert.type}
          title={successAlert.title}
          duration={3000}
          showIcon={false}
        />
      )}

      {/* Backdrop */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 9998,
          opacity: isAnimating ? 1 : 0,
          transition: 'opacity 0.3s ease-in-out'
        }}
        onClick={handleClose}
      />

      {/* Overlay Container */}
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: isAnimating 
            ? 'translate(-50%, -50%) scale(1)' 
            : 'translate(-50%, -50%) scale(0.9)',
          width: isMobile ? '95%' : '90%',
          // maxWidth: '1500px',
          maxWidth: isLargeDesktop ? '70%' : '90%',
          maxHeight: isMobile ? '95vh' : '95vh',
          backgroundColor: 'white',
          borderRadius: '12px',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          overflow: 'hidden',
          opacity: isAnimating ? 1 : 0,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: isMobile ? '0.75rem' : '1rem 1rem',
          borderBottom: '1px solid #e0e0e0',
          flexShrink: 0
        }}>
          <h1 style={{
            margin: 0,
            fontSize: isMobile ? '1rem' : '1.125rem',
            fontWeight: '500',
            letterSpacing: '-0.01em',
            cursor: 'default'
          }}>
            Screener Submission
          </h1>
           <FontAwesomeIcon 
             icon={faTimes} 
             style={{ 
               fontSize: isMobile ? '1.5rem' : '1.2rem', 
               cursor: 'pointer',
               padding: isMobile ? '0.5rem' : '0.2rem'
             }}
             onClick={handleClose}
           />
        </div>

        {/* Content */}
        <div style={{ 
          padding: isMobile ? '0.75rem' : '1rem',
          overflow: 'auto',
          flex: 1
        }}>
          {!currentInterview ? (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: '50vh' 
            }}>
              <Spinner size="lg" color="primary" />
            </div>
          ) : (
            <>
               {/* Job Title and Icon Section */}
               <div style={{
                //  backgroundColor: 'red',
                 height: '100%',
                 width: '100%',
                 marginBottom: '0.5rem'
               }}>
                 <div 
                   style={{
                     display: 'flex',
                     flexDirection: 'row',
                     alignItems: 'center',
                     gap: '0.5rem'
                   }}>
                     <img src="/svgs/expanded-view-icon.svg" alt="Expanded View Icon" style={{ width: '32px', height: '32px' }}/>
                     <p style={{ margin: 0, fontSize: '1rem', fontWeight: '500', color: '#3F4254' }}>{filters?.selectedJob?.title || currentInterview?.jobTitle}</p>
                 </div>
               </div>

               {(filters?.appliedQuestions !== null && filters?.appliedQuestions !== undefined) && currentResponses && currentResponses.length > 0 && (
                <div style={{
                  //  backgroundColor: 'red',
                   height: '100%',
                   width: '100%',
                   marginBottom: '1rem'
                 }}>
                   <div 
                     style={{
                       display: 'flex',
                       flexDirection: 'row',
                       alignItems: 'center',
                       gap: '0.5rem'
                     }}>
                       <p style={{ margin: 0, fontSize: '1rem', fontWeight: '500', color: '#3F4254' }}>Filters:</p>
                       <span style={{ 
                          backgroundColor: '#e9ecef', 
                          padding: '0.25rem 0.5rem', 
                          borderRadius: '4px',
                          fontSize: isMobile ? '0.75rem' : '0.875rem',
                          color: '#6C757D',
                          marginLeft: '0.5rem',
                        }}>
                          <b>Question:</b> {(() => {
                            const questionIndex = filters.appliedQuestions; // Already 0-based index
                            const question = currentResponses[questionIndex];
                            return question?.question?.text || `Question #${questionIndex + 1}`;
                          })()}
                      </span>
                   </div>
                 </div>
               )}

              <div style={{ 
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? '1.5rem' : '1.4rem',
              }}>
                {/* Left Container */}
                  <div style={{ 
                    flex: isMobile ? 'none' : (showContent ? '2.4' : '1'), 
                    display: 'flex', 
                    flexDirection: 'column',
                  }}>
                  <VideoPlayerSection
                    videoUrl={videoPlayerState.videoUrl}
                    contentType={videoPlayerState.contentType}
                    interviewContentType={selectedResponse?.answer?.video ? 'video' : selectedResponse?.answer?.audio ? 'audio' : (currentInterview?.contentType as 'video' | 'audio')}
                    thumbnailUrl={selectedResponse?.answer?.thumbnail || currentInterview.thumbnailUrl}
                    isPlaying={videoPlayerState.isPlaying}
                    onPlayToggle={handlePlayToggle}
                    isMobile={isMobile}
                    isMuted={isMuted}
                    setIsMuted={setIsMuted}
                    isDeleted={isCurrentInterviewDeleted}
                    deleteReason={currentDeleteReason}
                  />

                  <div style={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: isMobile ? 'flex-start' : 'center',
                    justifyContent: 'space-between',
                    gap: '1rem',
                    marginBottom: '1rem'
                  }}>
                    <ApplicantInfoSection 
                      applicantName={currentInterview.applicantName}
                      jobTitle={currentInterview.jobTitle}
                      dateApplied={currentInterview.dateApplied}
                      isMobile={isMobile}
                    />

                    <ActionButtonsSection 
                      onViewApplication={handleViewApplication}
                      onDownloadVideo={handleDownloadAllVideos}
                      onDownloadQuestion={(index) => handleDownloadQuestion(index, currentResponses[index] || null)}
                      onQuestionSelect={setSelectedQuestionIndex}
                      downloadingAll={downloadingAll}
                      downloadingQuestions={downloadingQuestions}
                      isRedirecting={isRedirecting}
                      selectedQuestionIndex={selectedQuestionIndex}
                      totalQuestions={currentResponses.length}
                      contentType={currentInterview?.contentType}
                      isDeleted={isCurrentInterviewDeleted}
                    />
                  </div>

                  <NavigationControlsSection 
                    currentIndex={currentInterviewIndex}
                    totalCount={videoData.length}
                    onPrevious={goToPrevious}
                    onNext={goToNext}
                    disabled={videoData.length <= 1}
                  />

                  <ApplicantThumbnailsSection 
                    videoData={videoData}
                    currentIndex={currentInterviewIndex}
                    onApplicantSelect={navigateToInterview}
                    isMobile={isMobile}
                  />
                </div>

                {/* Right Sidebar */}
                <RightSidebarSection 
                  activeTab={activeTab}
                  onTabChange={handleTabChange}
                  responses={currentResponses}
                  selectedQuestionIndex={selectedQuestionIndex}
                  onQuestionSelect={setSelectedQuestionIndex}
                  selectedResponse={selectedResponse}
                  onDownloadQuestion={handleDownloadQuestion}
                  downloadingQuestions={downloadingQuestions}
                  isMobile={isMobile}
                  contentType={currentInterview?.contentType}
                  showContent={showContent}
                  setShowContent={setShowContent}
                  isDeleted={isCurrentInterviewDeleted}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default ExpandedInterviewPageOverlay;

